<template>
  <div class="my-publish-page">
    <div class="mp-container">
      <!-- 标题 + Tabs -->
      <div class="mp-head">
        <span class="panel-bar"></span>
        <div class="mp-title">我的发布</div>
      </div>

      <div class="mp-tabs" role="tablist" aria-label="我的发布状态筛选">
        <button
          v-for="t in tabs"
          :key="t.value"
          class="mp-tab"
          :class="{ active: activeTab === t.value }"
          type="button"
          role="tab"
          :aria-selected="activeTab === t.value"
          @click="activeTab = t.value"
        >
          {{ t.label }}
        </button>
      </div>

      <!-- 列表 -->
      <div class="mp-list">
        <div v-for="task in filteredTasks" :key="task.id" class="mp-card">
          <div style="display:flex; justify-content: space-between;">
            <div class="mp-name">
              {{ task.title }}
            </div>
            <div class="mp-status" :class="'is-' + task.status">
              {{ statusText(task.status) }}
            </div>
          </div>

          <div class="mp-desc">
            {{ task.desc }}
          </div>

          <!-- 标签 + 统计 -->
          <div class="mp-meta">
            <div class="mp-tags">
              <span v-for="tag in task.tags" :key="tag" class="mp-tag">{{ tag }}</span>
            </div>
          </div>

          <!-- 行信息 -->
          <div class="mp-row">
            <div class="mp-row-item">
              <span class="k">截止时间</span>
              <span class="v">{{ task.deadline }}</span>
            </div>
            <div class="mp-row-item">
              <span class="k">预算</span>
              <span class="v">￥{{ task.budget }}</span>
            </div>
            <div class="mp-row-item">
              <span class="k">用途</span>
              <span class="v">{{ task.purpose === 'business' ? '商用' : '个人' }}</span>
            </div>
            <div class="mp-row-item">
              <span class="k">位置</span>
              <span class="v">{{ task.location || '线上' }}</span>
            </div>
          </div>

          <!-- 子任务（灰底块） -->
          <div v-if="task.subtasks?.length" class="mp-subtasks">
            <div v-for="st in task.subtasks" :key="st.id" class="mp-subtask">
              <div class="st-title">{{ st.title }}</div>
              <div class="st-desc">{{ st.desc }}</div>
              <div class="st-row">
                <span>截止时间 {{ st.deadline || '未设置' }}</span>
                <span>预算 ￥{{ st.price }}</span>
                <span>用途 {{ task.purpose === 'business' ? '商用' : '个人' }}</span>
                <span>位置 {{ task.location || '线上' }}</span>
              </div>
            </div>
          </div>

          <!-- 底部：投标人数 + 右侧按钮 -->
          <div class="mp-foot">
            <div class="mp-foot-left">
              投标人数：<span class="num">{{ task.applyCount }}</span> 人
              <span class="link" @click="onViewApplicants(task)">查看</span>
            </div>

            <div class="mp-actions">
              <!-- 待接单 -->
              <template v-if="task.status === 'pending'">
                <el-button type="success" plain class="btn solid" @click="onViewApplicants(task)">
                  查看投标人信息
                </el-button>
                <el-button type="success" plain class="btn" @click="onContact(task)">联系平台</el-button>
              </template>

              <!-- 处理中 -->
              <template v-else-if="task.status === 'processing'">
                <el-button type="success" plain class="btn solid" @click="onContact(task)">联系平台</el-button>
                <el-button type="success" class="btn" >申请验收</el-button>
              </template>

              <!-- 已完成 -->
              <el-button v-else-if="task.status === 'done'" type="success" plain class="btn solid" @click="onContact(task)">
                联系平台
              </el-button>

              <!-- 异常 -->
              <el-button
                v-else-if="task.status === 'exception'"
                type="success"
                plain
                class="btn solid"
                @click="onContact(task)"
              >
                联系平台
              </el-button>
            </div>
          </div>
        </div>

        <div v-if="!filteredTasks.length" class="mp-empty">
          暂无数据
        </div>
      </div>
    </div>

    <!-- 已报价人员列表弹窗（按 UI 还原） -->
    <el-dialog
      v-model="applicantOpen"
      width="860px"
      class="bidder-dialog"
      :show-close="false"
      :close-on-click-modal="true"
      :teleported="false"
      align-center
    >
      <template #header>
        <div class="bidder-hd">
          <div class="bidder-title">已报价人员列表</div>
          <img
            class="bidder-close"
            src="@/assets/Icon／Outline／x@2x.png"
            alt=""
            role="button"
            tabindex="0"
            @click="applicantOpen = false"
            @keyup.enter="applicantOpen = false"
          />
        </div>
      </template>

      <div class="bidder-body">
        <el-table
          :data="pagedBidders"
          class="bidder-table"
          header-row-class-name="bidder-table__headrow"
          :border="false"
          :cell-style="tableCellStyle"
          :header-cell-style="tableHeaderCellStyle"
        >
          <el-table-column prop="quoteTime" label="报价时间" min-width="180" />
          <el-table-column prop="amount" label="报价金额" min-width="120" />
          <el-table-column prop="eta" label="预计完成时间" min-width="180" />

          <el-table-column
            label="操作"
            min-width="120"
            align="center"
            class-name="bidder-col--op"
          >
            <template #default="{ row }">
              <span class="bidder-op" @click="onWantContact(row)">我要联系</span>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页：整体统一右对齐 -->
        <div class="bidder-ft">
          <div class="bidder-ft-left">
            <span>共</span>
            <span class="n">{{ bidderPager.total }}</span>
            <span>条</span>

            <el-select v-model="bidderPager.pageSize" class="bidder-pagesize" size="small" :teleported="false">
              <el-option v-for="s in pageSizes" :key="s" :label="`${s} / 页`" :value="s" />
            </el-select>
          </div>

          <el-pagination
            v-model:current-page="bidderPager.page"
            :page-size="bidderPager.pageSize"
            :total="bidderPager.total"
            layout="prev, pager, next"
            class="bidder-pagination"
            :teleported="false"
          />

          <div class="bidder-ft-right">
            <span>前往</span>
            <el-input v-model="gotoPageInput" size="small" class="bidder-goto" @keyup.enter="onGotoPage" />
            <span>页</span>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'

/** Tabs（按 UI） */
const tabs = [
  { label: '全部', value: 'all' },
  { label: '待接单', value: 'pending' },
  { label: '处理中', value: 'processing' },
  { label: '已完成', value: 'done' },
  { label: '异常', value: 'exception' }
]
const activeTab = ref('all')

/** 示例数据：后续替换为接口返回 */
const tasks = ref([
  {
    id: 't1',
    title: '任务名称任务名称任务名称任务名称任务名称任务名称',
    desc: '任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍',
    status: 'pending', // pending | processing | done | exception
    tags: ['标签1', '专科(高职)', '艺术类', '民办', '985', '211', '强基计划', '保研率:12'],
    viewCount: 985,
    favCount: 211,
    planCount: 12,
    leftCount: 2,
    deadline: '2026-01-01',
    budget: 1000,
    purpose: 'business', // personal | business
    location: '',
    applyCount: 5,
    subtasks: []
  },
  {
    id: 't2',
    title: '任务名称任务名称任务名称任务名称任务名称任务名称',
    desc: '任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍',
    status: 'processing',
    tags: ['标签1', '专科(高职)', '艺术类', '民办', '985', '211', '强基计划', '保研率:12'],
    viewCount: 200,
    favCount: 12,
    planCount: 0,
    leftCount: 0,
    deadline: '2026-01-01',
    budget: 1000,
    purpose: 'personal',
    location: '线上',
    applyCount: 5,
    subtasks: [
      {
        id: 'st1',
        title: '子任务1',
        desc: '任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍',
        price: 500,
        deadline: '2026-01-01'
      },
      {
        id: 'st2',
        title: '子任务2',
        desc: '任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍',
        price: 500,
        deadline: '2026-01-01'
      }
    ]
  },
  {
    id: 't3',
    title: '任务名称任务名称任务名称任务名称任务名称任务名称',
    desc: '任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍',
    status: 'done',
    tags: ['标签1', '专科(高职)', '艺术类', '民办', '985', '211', '强基计划', '保研率:12'],
    viewCount: 50,
    favCount: 3,
    planCount: 0,
    leftCount: 0,
    deadline: '2026-01-01',
    budget: 1000,
    purpose: 'business',
    location: '',
    applyCount: 2,
    subtasks: []
  },
  {
    id: 't4',
    title: '任务名称任务名称任务名称任务名称任务名称任务名称',
    desc: '任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍任务介绍',
    status: 'exception',
    tags: ['标签1', '专科(高职)', '艺术类', '民办', '985', '211', '强基计划', '保研率:12'],
    viewCount: 30,
    favCount: 0,
    planCount: 0,
    leftCount: 0,
    deadline: '2026-01-01',
    budget: 1000,
    purpose: 'personal',
    location: '',
    applyCount: 1,
    subtasks: []
  }
])

const filteredTasks = computed(() => {
  if (activeTab.value === 'all') return tasks.value
  return tasks.value.filter((t) => t.status === activeTab.value)
})

function statusText(s) {
  return (
    {
      pending: '待接单',
      processing: '处理中',
      done: '已完成',
      exception: '异常'
    }[s] || '未知'
  )
}

/** 查看投标人弹窗 */
const applicantOpen = ref(false)
const currentTask = ref(null)

/** 投标人列表（示例数据，后续换接口） */
const bidders = ref([
  { id: 'b1', quoteTime: '2026-01-01 12:12:12', amount: '￥1000', eta: '2026-01-03 12:12:12' },
  { id: 'b2', quoteTime: '2026-01-01 12:12:12', amount: '￥1000', eta: '2026-01-03 12:12:12' },
  { id: 'b3', quoteTime: '2026-01-01 12:12:12', amount: '￥1000', eta: '2026-01-03 12:12:12' },
  { id: 'b4', quoteTime: '2026-01-01 12:12:12', amount: '￥1000', eta: '2026-01-03 12:12:12' },
  { id: 'b5', quoteTime: '2026-01-01 12:12:12', amount: '￥1000', eta: '2026-01-03 12:12:12' },
  { id: 'b6', quoteTime: '2026-01-01 12:12:12', amount: '￥1000', eta: '2026-01-03 12:12:12' },
  { id: 'b7', quoteTime: '2026-01-01 12:12:12', amount: '￥1000', eta: '2026-01-03 12:12:12' },
  { id: 'b8', quoteTime: '2026-01-01 12:12:12', amount: '￥1000', eta: '2026-01-03 12:12:12' },
  { id: 'b9', quoteTime: '2026-01-01 12:12:12', amount: '￥1000', eta: '2026-01-03 12:12:12' },
  { id: 'b10', quoteTime: '2026-01-01 12:12:12', amount: '￥1000', eta: '2026-01-03 12:12:12' },
  { id: 'b8', quoteTime: '2026-01-01 12:12:12', amount: '￥1000', eta: '2026-01-03 12:12:12' },
  { id: 'b9', quoteTime: '2026-01-01 12:12:12', amount: '￥1000', eta: '2026-01-03 12:12:12' },
  { id: 'b10', quoteTime: '2026-01-01 12:12:12', amount: '￥1000', eta: '2026-01-03 12:12:12' }
])

const pageSizes = [10, 20, 50, 100]
const bidderPager = ref({
  page: 1,
  pageSize: 10,
  total: 0
})
const gotoPageInput = ref('')

watch(
  () => bidders.value.length,
  (len) => {
    bidderPager.value.total = len
  },
  { immediate: true }
)

const pagedBidders = computed(() => {
  const start = (bidderPager.value.page - 1) * bidderPager.value.pageSize
  return bidders.value.slice(start, start + bidderPager.value.pageSize)
})

watch(
  () => bidderPager.value.pageSize,
  () => {
    bidderPager.value.page = 1
  }
)

function onViewApplicants(task) {
  currentTask.value = task
  applicantOpen.value = true

  bidderPager.value.page = 1
  gotoPageInput.value = ''
}

function onWantContact(row) {
  ElMessage.success(`我要联系（报价时间：${row.quoteTime}）`)
}

function onGotoPage() {
  const v = Number(String(gotoPageInput.value || '').trim())
  if (!Number.isFinite(v) || v <= 0) return
  const max = Math.max(1, Math.ceil(bidderPager.value.total / bidderPager.value.pageSize))
  bidderPager.value.page = Math.min(max, Math.max(1, v))
}

function onContact(task) {
  ElMessage.success(`联系平台（任务 ${task.id}）`)
}

// async function onConfirmDone(task) {
//   try {
//     await ElMessageBox.confirm('确认该任务已完成？', '提示', {
//       type: 'warning',
//       confirmButtonText: '申请验收',
//       cancelButtonText: '取消'
//     })
//     task.status = 'done'
//     ElMessage.success('已标记为完成')
//   } catch {
//     // cancel
//   }
// }

/** 表头/单元格样式（按你最新要求） */
function tableHeaderCellStyle() {
  return {
    background: '#F2FFF2',
    fontWeight: 500,
    fontSize: '12px',
    color: '#4F6750',
    height: '52px',
    borderBottom: '1px solid #EDEDED'
  }
}

/**
 * body 单元格：默认应用“列表字体样式”
 * 注意：操作列用 class-name=bidder-col--op 在 CSS 里单独覆盖颜色
 */
function tableCellStyle() {
  return {
    fontWeight: 400,
    fontSize: '12px',
    color: '#515A6E',
    height: '52px',
    borderBottom: '1px solid #F2F2F2'
  }
}
</script>

<style lang="scss" scoped>
.my-publish-page {
  width: 100%;
  background: #fff;
  padding: 0 32px;
}

.mp-container {
  width: 100%;
}

/* ===== Header ===== */
.mp-head {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 24px;
}

.panel-bar {
  width: 3px;
  height: 16px;
  border-radius: 2px;
  background: #4caf50;
}

.mp-title {
  font-weight: 500;
  font-size: 18px;
  color: #495060;
}

/* ===== Tabs（按 UI 自绘） ===== */
.mp-tabs {
  display: flex;
  align-items: center;
  gap: 26px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.mp-tab {
  position: relative;
  font-size: 16px;
  color: #606266;
  cursor: pointer;
  user-select: none;
  padding: 4px 0;
  border: 0;
  background: transparent;

  &.active {
    color: #4caf50;
    font-weight: 500;
  }

  &.active::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -11px;
    height: 2px;
    background: #4caf50;
    border-radius: 2px;
  }
}

/* ===== List ===== */
.mp-list {
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 35px;
}

.mp-card {
  position: relative;
  padding-top: 14px;
  border-top: 1px solid #f3f3f3;

  &:first-child {
    border-top: none;
    padding-top: 0;
  }
}

/* 右上角状态 */
.mp-status {
  font-size: 18px;
  font-weight: 400;

  &.is-pending {
    color: #e81111;
  }

  &.is-processing {
    color: #2f9f35;
  }

  &.is-done {
    color: #b8b8b8;
  }

  &.is-exception {
    color: #b8b8b8;
  }
}

/* 标题、描述 */
.mp-name {
  font-size: 18px;
  color: #333333;
  margin-bottom: 16px;
  padding-right: 90px;
}

.mp-desc {
  font-size: 14px;
  color: #8b8b8b;
  line-height: 1.7;
  margin-bottom: 19px;
}

/* tags + stats */
.mp-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.mp-tags {
  display: flex;
  align-items: center;
  gap: 19px;
  flex-wrap: wrap;
}

.mp-tag {
  height: 24px;
  background: #f5f6f7;
  border-radius: 4px 4px 4px 4px;
  line-height: 22px;
  padding: 0 29px;
  font-size: 14px;
  color: #666666;
}

/* 行信息 */
.mp-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 18px;
  font-size: 14px;
  color: #8b8b8b;
  margin-bottom: 45px;

  .mp-row-item {
    display: inline-flex;
    gap: 4px;
    align-items: center;

    .v {
      font-size: 14px;
      color: #333333;
    }
  }
}

/* 子任务灰底块 */
.mp-subtasks {
  background: #f6f6f6;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 55px;
}

.mp-subtask {
  .st-title {
    font-size: 12px;
    font-weight: 700;
    color: #2e323e;
    margin-bottom: 6px;
  }

  .st-desc {
    font-size: 12px;
    color: #9a9a9a;
    line-height: 1.7;
    margin-bottom: 8px;
  }

  .st-row {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    font-size: 12px;
    color: #9a9a9a;
  }
}

/* 底部：左信息 + 右按钮 */
.mp-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mp-foot-left {
  font-weight: 400;
  font-size: 14px;
  color: #8b8b8b;

  .link {
    color: #4caf50;
    margin-left: 10px;
    cursor: pointer;
    user-select: none;
  }
}

.mp-actions {
  display: inline-flex;
  align-items: center;
  gap: 28px;

  flex: 0 0 auto;

  :deep(.el-button.btn) {
    height: 41px;
    background: #4caf50;
    color: #ffffff;
    padding: 0 18px;
  }

  :deep(.el-button.btn.solid) {
    background: #ffffff;
    border-radius: 4px 4px 4px 4px;
    border: 1px solid #4caf50;
    font-weight: 400;
    font-size: 16px;
    color: #4caf50;
  }
}

.mp-empty {
  padding: 28px 0;
  text-align: center;
  color: #9a9a9a;
  font-size: 13px;
}

/* ===== 已报价人员列表 Dialog（按 UI 还原） ===== */
.bidder-dialog {
  :deep(.el-dialog) {
    border-radius: 8px;
  }

  :deep(.el-dialog__header) {
    padding: 0px;
    margin: 0;
  }

  :deep(.el-dialog__body) {
    padding: 0;
  }
}

.bidder-hd {
  padding-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eeeeee;
  background: #ffffff;
}

.bidder-title {
  font-weight: 500;
  font-size: 18px;
  color: #333333;
}

.bidder-close {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.bidder-body {
  padding: 18px;
  padding-top: 8px;
}

.bidder-table {
  width: 100%;

  :deep(.el-table) {
    --el-table-border-color: transparent;
  }

  :deep(.el-table__inner-wrapper::before) {
    height: 0;
  }

  :deep(.el-table__row:hover > td.el-table__cell) {
    background: #f0fff1;
  }

  /* 行高 52px：补一层兜底，避免部分主题/内联 style 不生效 */
  :deep(.el-table__header th.el-table__cell),
  :deep(.el-table__body td.el-table__cell) {
    height: 52px;
    padding-top: 0;
    padding-bottom: 0;
  }

  /* 操作列不套用“列表字体颜色”（只改颜色，不改字号/字重） */
  :deep(td.el-table__cell.bidder-col--op),
  :deep(.bidder-col--op) {
    color: inherit;
  }
}

.bidder-op {
  color: #4caf50;
  cursor: pointer;
  user-select: none;
  font-size: 12px;
  font-weight: 400;

  &:hover {
    text-decoration: underline;
  }
}

/* 分页区域：整体统一右对齐（如图） */
.bidder-ft {
  padding: 24px 0 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  flex-wrap: wrap;
}

.bidder-ft-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #8b8b8b;

  .n {
    color: #333;
  }
}

.bidder-pagesize {
  width: 80px;
}

.bidder-pagination {
  :deep(.el-pager li) {
    min-width: 28px;
    height: 28px;
    line-height: 28px;
    border-radius: 2px;
    font-size: 12px;
  }

  :deep(.el-pager li.is-active) {
    background: #4caf50;
    color: #fff;
  }

  :deep(.btn-prev),
  :deep(.btn-next) {
    width: 28px;
    height: 28px;
    border-radius: 2px;
  }
}

.bidder-ft-right {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #8b8b8b;
}

.bidder-goto {
  width: 56px;

  :deep(.el-input__wrapper) {
    border-radius: 2px;
  }
}
</style>